import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Edit2 } from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Table from '../components/Table';
import Modal from '../components/Modal';
import { FF, ErrMsg } from '../components/FormField';
import { FilterBar, SearchInput, PageHeader } from '../components/FilterBar';
import CompanyDeptFilter from '../components/CompanyDeptFilter';

// LI at module level — prevents focus loss
const LI = ({ label, name, form, setForm, type = 'text', ta, placeholder }) => (
  <div>
    <label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>{label}</label>
    {ta
      ? <textarea value={form[name]||''} onChange={e => setForm({...form,[name]:e.target.value})} rows={3} placeholder={placeholder} className="input-dark"/>
      : <input type={type} value={form[name]||''} onChange={e => setForm({...form,[name]:e.target.value})} placeholder={placeholder} className="input-dark"/>
    }
  </div>
);

export default function Donations() {
  const { isIT, isAdmin, isDirector, isFinance } = useAuth();
  const showCompanyFilter = isAdmin || isDirector || isFinance;
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [pages, setPages] = useState(1); const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false); const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');
  const [filterCompany, setFilterCompany] = useState([]); const [filterDept, setFilterDept] = useState(null);
  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (dateFrom) params.from = dateFrom; if (dateTo) params.to = dateTo;
      if (filterCompany?.length) params.company_id = Array.isArray(filterCompany) ? filterCompany.join(',') : filterCompany;
      if (filterDept) params.department_id = filterDept;
      const r = await api.get('/donations', { params });
      setRows(r.data.data || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, search, dateFrom, dateTo, filterCompany, filterDept]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      selected ? await api.put(`/donations/${selected.id}`, form) : await api.post('/donations', form);
      setModal(null); setSelected(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const columns = [
    { header: 'Asset', render: r => r.asset
      ? <div><div className="font-mono text-xs" style={{color:'#60a5fa'}}>{r.asset.asset_number}</div><div className="text-xs" style={{color:'#475569'}}>{r.asset.brand} {r.asset.model}</div></div>
      : <span style={{color:'#334155'}}>{r.asset_id}</span>
    },
    { header: 'Donated To', render: r => <span style={{color:'#e2e8f0'}}>{r.donated_to}</span> },
    { header: 'Date', key: 'donated_date' },
    { header: 'Approved By', render: r => <span style={{color:'#60a5fa'}}>{r.approved_by || <span style={{color:'#334155'}}>—</span>}</span> },
    { header: 'Type', render: r => <span className="text-xs" style={{color:'#94a3b8'}}>{r.reason?.includes('Offboarding') ? 'Offboarding' : 'Regular'}</span> },
    { header: 'Reason', render: r => <span className="text-xs truncate max-w-xs block" style={{color:'#94a3b8'}}>{r.reason||'—'}</span> },
    { header: '', render: r => isIT && <button onClick={() => { setSelected(r); setForm({...r}); setModal('form'); }} className="btn-ghost px-2 py-1 text-xs"><Edit2 size={12}/></button> }
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="Donations" count={total}>
        <div className="flex items-center gap-2 flex-wrap">
          {showCompanyFilter && <CompanyDeptFilter companyId={filterCompany} deptId={filterDept} onChange={(c,d) => { setFilterCompany(c); setFilterDept(d); setPage(1); }}/>}
          <button onClick={load} className="btn-ghost"><RefreshCw size={14}/></button>
          {isIT && <button onClick={() => { setSelected(null); setForm({ donated_date: new Date().toISOString().split('T')[0] }); setModal('form'); }} className="btn-primary"><Plus size={14}/> Add Donation</button>}
        </div>
      </PageHeader>
      <FilterBar hasFilters={!!(search||dateFrom||dateTo)} onClear={() => { setSearch(''); setDateFrom(''); setDateTo(''); setPage(1); }}>
        <SearchInput value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search donated to, reason..." />
        <DateRangePicker from={dateFrom} to={dateTo} onChange={(f,t) => { setDateFrom(f); setDateTo(t); setPage(1); }}/>
      </FilterBar>
      <Table columns={columns} data={rows} loading={loading} page={page} pages={pages} total={total} onPage={setPage}/>
      {modal === 'form' && (
        <Modal title={selected ? 'Edit Donation' : 'Add Donation'} onClose={() => { setModal(null); setSelected(null); }}>
          <form onSubmit={handleSave} className="space-y-3">
            <ErrMsg msg={error}/>
            <div className="grid grid-cols-2 gap-3">
              <LI form={form} setForm={setForm} label="Asset ID *" name="asset_id" placeholder="Asset UUID or number"/>
              <LI form={form} setForm={setForm} label="Donated To *" name="donated_to" placeholder="Name of recipient"/>
              <LI form={form} setForm={setForm} label="Donation Date *" name="donated_date" type="date"/>
              <LI form={form} setForm={setForm} label="Approved By *" name="approved_by" placeholder="Name of approving person"/>
            </div>
            <LI form={form} setForm={setForm} label="Reason" name="reason" ta placeholder="Why is this asset being donated?"/>
            <LI form={form} setForm={setForm} label="Remarks" name="remarks" ta placeholder="Additional notes..."/>
            {!selected && (
              <div className="px-3 py-2.5 rounded-xl text-xs" style={{background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.2)',color:'#fbbf24'}}>
                ⚠ Saving this donation will automatically mark the asset as <strong>Derecognized</strong> in financial records and reports (Disposal / WDV tabs).
              </div>
            )}
            <div className="flex gap-3">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
